"use client";

import React, { useState } from "react";
import Image from "@components/atoms/Image";
import Button from "@components/atoms/Button";
import styles from "./AllFeatureCard.module.scss";
import { AllFeatureCardProps, FeatureItem } from "./AllFeatureCard.types";
import assetLoader from "@utils/internalImageLoder";
import MileageModal from "../MileageModal";
import { formatDeliveryDate } from "@utils/formatDate";

export const AllFeatureCard: React.FC<AllFeatureCardProps> = ({
  feature,
  isDetailed = false,
  className = "",
  isSellable,
  isMMV = false,
  isInApp = false,
}) => {
  const [openVariantModal, setOpenVariantModal] = useState<boolean>(false);
  const handleVariantClick = () => {
    setOpenVariantModal(true);
  };
  const variants = (feature.available || []).map((variant: any) => ({
    name: variant.name,
    variantName: variant.variantName,
    fuelType: variant.fuelType,
    transmission: variant.transmissionType,
    onRoadPrice: variant.onRoadPrice,
    slug: variant.slug,
    mileage: variant.mileage ? `${variant.mileage.value}` : null,
    expressDelivery: variant.expressDeliveryAvailable,
    standardDelivery: formatDeliveryDate(variant.expressDeliveryDays || variant.standardDeliveryDays),
    status: variant.status
  }));
  
  if (isInApp) {
    return (
      <div className={`${styles.appCard} ${className}`}>
        <div className={styles.appImageContainer}>
          <Image
            src={feature.image}
            alt={feature.title}
            width={320}
            height={240}
            className={styles.appImage}
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  if (isDetailed) {
    return (
      <div className={`${styles.detailedCard} ${className}`}>
        <div className={styles.detailedImageContainer}>
          <Image
            src={feature.image}
            alt={feature.title}
            width={384}
            height={440}
            className={styles.detailedImage}
            loading="lazy"
          />
          <div className={styles.gradientOverlay} />
        </div>

        {variants.length > 0 && !isMMV && (
          <Button className={styles.variantButton} onClick={handleVariantClick}>
            <span className={styles.variantText}>{feature.availableVariants}</span>
            <Image
              src={`${assetLoader()}/icons/chevron-right-dark.svg`}
              alt="Right Arrow"
              width={16}
              height={16}
              className={styles.arrowIcon}
              loading="lazy"
            />
          </Button>
        )}

        <div className={styles.detailedContent}>
          <p className={styles.detailedDescription}>{`${feature.title} ${feature.description}`}</p>
        </div>
        {openVariantModal && variants.length > 0 && (
          <MileageModal
            isOpen={openVariantModal}
            onClose={() => {
              setOpenVariantModal(false);
            }}
            title={`Available in ${feature.available.length} variants`}
            customStyles={{
              modalOverlay: styles.modalOverlay,
              modalContent: styles.modalContainer,
            }}
            variants={variants}
            preventScoll={true}
            isSellable={isSellable}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.simpleCard} ${className}`}>
      <div className={styles.simpleImageContainer}>
        <Image
          src={feature.image}
          alt={feature.title}
          width={384}
          height={300}
          className={styles.simpleImage}
          loading="lazy"
          fetchPriority="low"
          priority={false}
          quality={75}
        />
      </div>
      {variants.length > 0 && (
        <Button className={styles.variantButton} onClick={handleVariantClick}>
          <span className={styles.variantText}>{feature.availableVariants}</span>
          <Image
            src={`${assetLoader()}/icons/expertReview/Chevron_right.svg`}
            alt="Right Arrow"
            width={16}
            height={16}
            className={styles.arrowIcon}
            loading="lazy"
          />
        </Button>
      )}

      <h3 className={styles.simpleTitle}>{feature.title}</h3>
      <p className={styles.simpleDescription}>{feature.description}</p>

      {openVariantModal && variants.length > 0 && (
        <MileageModal
          isOpen={openVariantModal}
          onClose={() => {
            setOpenVariantModal(false);
          }}
          title={`Available in ${feature.available.length} variants`}
          customStyles={{
            modalOverlay: styles.modalOverlay,
            modalContent: styles.modalContainer,
          }}
          variants={variants}
          preventScoll={true}
          isSellable={isSellable}
        />
      )}
    </div>
  );
}; 